/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';

import { BrandColors, LoneIconButton, Text } from '@blend/enterprise-components';
import { Attachment } from '../../types';
import { useParams } from 'react-router-dom';
import { truncate } from 'lodash';
import { downloadDocument } from 'blb-api/loans';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

const styles = StyleSheet.create({
  attachmentItem: {
    alignItems: 'center',
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    flexBasis: '49.5%',
    justifyContent: 'space-between',
    marginTop: 8,
    padding: '10px 24px',
  },
  attachmentList: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  singleItemPerRow: {
    ':first-child': {
      marginTop: 0,
    },
    flexBasis: '100%',
  },
});

interface BaseProps {
  attachments: Attachment[];
  singleItemPerRow?: boolean;
}

interface RemoveDisabledProps {
  disableRemoveOption: true;
}

interface RemoveEnabledProps {
  disableRemoveOption?: false;
  handleRemove: (index: number) => void;
}

export type AttachmentRepeaterProps = BaseProps & (RemoveEnabledProps | RemoveDisabledProps);
export const AttachmentRepeater: React.FC<AttachmentRepeaterProps> = props => {
  const { loanId } = useParams<{ loanId: string }>();
  const Toaster = useToasts();

  const openAttachment = (docId: string) => {
    downloadDocument({ documentId: docId, loanId, Toaster });
  };

  return (
    <ul className={css(styles.attachmentList)}>
      {props.attachments.map((attachment: Attachment, index: number) => {
        return (
          <li
            className={css(styles.attachmentItem, props.singleItemPerRow && styles.singleItemPerRow)}
            data-it="AttachmentRepeater-ListItem"
            key={`removeButton-${index}`}
            onClick={() => openAttachment(attachment.documentId)}
          >
            <Text colorName="SKY_TONE_45" fontSize="md" weight="bold">
              {truncate(attachment.name, { length: 40 })}
            </Text>
            {!props.disableRemoveOption && (
              <LoneIconButton
                btnSize="xs"
                data-it={`AttachmentRepeater-removeButton-${index}`}
                icon="clear"
                isCircle
                onClick={e => {
                  e.stopPropagation();
                  props.handleRemove(index);
                }}
                theme="tertiary"
                type="button"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};
